import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ApiService } from 'src/app/services/api.service';
import { CaptchaService } from 'src/app/services/captcha.service';
import { SharedService } from 'src/app/services/shared.service';
import { ExistingPaymentSourcesComponent } from 'src/app/shared/existing-payment-sources/existing-payment-sources.component';
import { GenerateStripeElementComponent } from 'src/app/shared/generate-stripe-element/generate-stripe-element.component';

@Component({
  selector: 'app-share-link-component',
  templateUrl: './share-link-component.component.html',
  styleUrl: './share-link-component.component.scss'
})
export class ShareLinkComponent implements OnInit {
  public businessName: string | null = null;
  public accountId: string | null = null;
  public isLoading = true;
  public isProcessingPayment = false;
  public isPaymentSuccessful = false;
  public selectedCard: any = null;
  public chargeAmount: number | null = null;
  public paymentSources: any[] = [];
  public patientName: string | null = null;
  public patientEmail = '';
  public patientPhone = '';
  public planAmount: number | null = null;
  public remainingBalance: number | null = null;
  public nextPaymentDate: string | null = null;
  public planDuration: string | null = null;
  public monthlyPayment: number | null = null;
  public enrollmentDate: string | null = null;
  public paymentStatus = 'Pending';
  public pfStatus: number | null = null;
  public readonly supportPhone = '+1 (833) 904-7001';
  public readonly supportEmail = 'collections@recuvery.com';
  public readonly copyrightYear = new Date().getFullYear();

  private doctorId: number | null = null;
  reveiveData: any;
  shareToken: string | null;
  captcha: any;
  practiceName: any;
  contractCode: any;

  constructor(
    private modalService: BsModalService,
    private apiService: ApiService,
    private cs: CaptchaService,
    private sharedService: SharedService,
    private route: ActivatedRoute
  ) {
    this.shareToken = this.route.snapshot.paramMap.get('token') || this.route.snapshot.queryParamMap.get('token');
  }

  async ngOnInit(): Promise<void> {
    this.captcha = await this.cs.getCaptchaToken();
    this.getRecuveryFeeData();
  }

  private getRecuveryFeeData(): void {
    this.apiService.api({
      captcha: this.captcha,
      contract_patient_token: String(this.shareToken)
    }, 'recuvery_fee', 'post').subscribe({
      next: (response: any) => {
        if (response?.is_error) {
          this.sharedService.customPopups(response?.original_text || 'Unable to load share details.', 1);
          this.isLoading = false;
          return;
        }

        const responseData = response?.data || response?.response?.data || response;
        if (!responseData) {
          this.isLoading = false;
          return;
        }

        const sprData = responseData?.spr_data || {};
        this.reveiveData = sprData;
        this.businessName = sprData.doctor_name || this.businessName;
        this.accountId =   sprData.contract_patient_token;
        this.contractCode = sprData.contract_code;
        this.chargeAmount = sprData.recuvery_fee ?? this.chargeAmount;
        this.patientName = this.resolvePatientName(sprData);
        this.practiceName = sprData.practice_name || this.practiceName;
        this.patientEmail = sprData.patient_email || sprData.email || '';
        this.patientPhone = this.resolvePatientPhone(sprData);
        this.planAmount = this.toNumberOrNull(
          sprData.plan_amount ?? sprData.contract_amount ?? sprData.total_amount
        );
        this.remainingBalance = this.toNumberOrNull(
          sprData.remaining_balance ?? sprData.remaining_amount ?? sprData.pending_amount
        );
        this.nextPaymentDate = sprData.next_payment_date || sprData.upcoming_payment_date || null;
        this.planDuration = this.resolvePlanDuration(sprData);
        this.monthlyPayment = this.toNumberOrNull(
          sprData.monthly_payment ?? sprData.monthly_amount ?? sprData.installment_amount
        );
        this.enrollmentDate = sprData.date_added ?? sprData.enrollment_date ?? null;
        this.applyPaymentStatus(sprData);
        this.doctorId = sprData.doctor_id || this.doctorId;
        if (this.isLinkExpired(responseData, sprData)) {
          this.isPaymentSuccessful = true;
        }
        this.paymentSources = this.extractPaymentSources(responseData, sprData);
        if (this.selectedCard?.source_id) {
          this.selectedCard = this.paymentSources.find((card: any) => card?.source_id === this.selectedCard?.source_id) || null;
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.sharedService.customPopups('Unable to load share details. Please refresh and try again.', 1);
      },
    });
  }

  public get summaryPlanAmount(): number | null {
    return this.planAmount ?? this.chargeAmount;
  }

  public payBusinessShare(): void {
    if (this.isProcessingPayment || this.isPaymentSuccessful) {
      return;
    }

    if (this.paymentSources.length) {
      this.openExistingCardsModal();
      return;
    }

    this.openStripeSetupModal();
  }

  private openExistingCardsModal(): void {
    const stripeProfiles = this.getShareLinkStripeProfile();
    const modalRef: BsModalRef<ExistingPaymentSourcesComponent> = this.modalService.show(ExistingPaymentSourcesComponent, {
      initialState: {
        extra_data: {
          cards_list: this.paymentSources,
          selectedCard: this.selectedCard,
          patient_profile: stripeProfiles.patient_profile,
          payment_source_type: this.sharedService.paymentSourceType.CARD,
          payment_mode: this.sharedService.paymentMode.BUSINESS,
          payment_type: 'share_link',
          captcha: this.captcha,
          doctor_id: this.doctorId || undefined,
          onAddCard: () => {
            setTimeout(() => this.openStripeSetupModal(), 150);
          },
        },
      },
      class: 'modal-dialog-centered',
    });

    modalRef.onHidden?.subscribe(() => {
      const cardSelected = this.normalizePaymentSource(modalRef.content?.extra_data?.selectedCard);
      if (modalRef.content?.isSelectionSubmitted && cardSelected?.source_id) {
        this.selectedCard = cardSelected;
        this.chargeBusinessShare();
      }
    });
  }

  private openStripeSetupModal(): void {
    const stripeProfiles = this.getShareLinkStripeProfile();
    const modalRef: BsModalRef<GenerateStripeElementComponent> = this.modalService.show(GenerateStripeElementComponent, {
      initialState: {
        stripeData: {
          payment_source_type: this.sharedService.paymentSourceType.CARD,
          payment_mode: this.sharedService.paymentMode.BUSINESS,
          payment_type: 'share_link',
          captcha: this.captcha,
          doctor_id: this.doctorId || undefined,
          patient_profile: stripeProfiles.patient_profile,
          doctor_profile: stripeProfiles.doctor_profile,
        },
      },
    });

    modalRef.content?.sendPaymentSourceData.subscribe((response: any) => {
      if (response?.success) {
        modalRef.hide();
        this.onShareLinkCardAdded(response.selectedCard);
        return;
      }
      this.sharedService.customPopups('Card setup failed. Please try again.', 1);
      modalRef.hide();
    });
  }

  private getShareLinkStripeProfile(): { patient_profile: any; doctor_profile: any } {
    const data = this.reveiveData || {};

    return {
      patient_profile: {
        ...data,
        business_name: this.businessName,
        patient_email: this.patientEmail || data.patient_email || data.email,
        patient_first_name: data.patient_first_name || data.first_name,
        patient_last_name: data.patient_last_name || data.last_name,
        patient_address: data.patient_address || data.address,
        city: data.city,
        state: data.state || data.state_name,
        zip: data.zip || data.postal_code,
        country_code: data.country_code || 'US',
      },
      doctor_profile: {
        doctor_name: data.doctor_name || this.businessName || this.practiceName || this.patientName,
        doctor_email: data.doctor_email || data.patient_email || data.email || this.patientEmail,
        doctor_address: data.doctor_address || data.patient_address || data.address,
        city: data.city,
        state_name: data.state_name || data.state,
        zip: data.zip || data.postal_code,
        country_code: data.country_code || 'US',
      },
    };
  }

  private onShareLinkCardAdded(cardResponse: any): void {
    const normalizedCard = this.normalizePaymentSource(cardResponse);
    if (!normalizedCard?.source_id) {
      this.sharedService.customPopups('Card setup failed. Please try again.', 1);
      return;
    }

    this.selectedCard = normalizedCard;
    if (!this.paymentSources.some((card: any) => card?.source_id === normalizedCard.source_id)) {
      this.paymentSources = [...this.paymentSources, normalizedCard];
    }

    this.sharedService.customPopups('Card added successfully.', 0);

    setTimeout(() => {
      this.openExistingCardsModal();
    }, 500);

    setTimeout(() => {
      this.getRecuveryFeeData();
    }, 1500);
  }

  private normalizePaymentSource(source: any): any | null {
    if (!source) {
      return null;
    }

    const nested = source?.data;
    const card = nested && typeof nested === 'object' && !Array.isArray(nested)
      ? { ...source, ...nested }
      : Array.isArray(nested) && nested.length
        ? { ...source, ...nested[0] }
        : source;
    const sourceId = card?.source_id ?? card?.payment_source_id ?? card?.id;

    if (!sourceId) {
      return null;
    }

    return { ...card, source_id: sourceId };
  }

  private chargeBusinessShare(modalRef?: BsModalRef<GenerateStripeElementComponent>): void {
    if (!this.selectedCard?.source_id || !this.doctorId) {
      this.sharedService.customPopups('Missing payment details to process the share.', 1);
      return;
    }

    const payload: any = {
      doctor_id: this.doctorId,
      payment_mode: this.sharedService.paymentMode.BUSINESS,
      source_id: this.selectedCard.source_id,
      captcha: this.captcha,
      contract_patient_token: this.accountId,
    };

    if (this.chargeAmount !== null) {
      payload.amount = this.chargeAmount;
    }

    this.isProcessingPayment = true;
    this.apiService.api(payload, 'settle_debt', 'post').subscribe({
      next: (response: any) => {
        this.isProcessingPayment = false;
        if (!response?.is_error) {
          modalRef?.hide();
          this.isPaymentSuccessful = true;
          this.getRecuveryFeeData();
          return;
        }
        this.sharedService.customPopups(response?.original_text || 'Unable to process payment.', 1);
      },
      error: () => {
        this.isProcessingPayment = false;
        this.sharedService.customPopups('Unable to process payment. Please try again.', 1);
      },
    });
  }

  private extractPaymentSources(responseData: any, sprData: any): any[] {
    const paymentSources = responseData?.payment_sources || sprData?.payment_sources || [];
    if (!Array.isArray(paymentSources)) {
      return [];
    }
    return paymentSources.filter((card: any) => card?.source_id);
  }

  private isLinkExpired(responseData: any, sprData: any): boolean {
    const isLinkExpired = responseData?.is_link_expired ?? sprData?.is_link_expired;
    return isLinkExpired === 1 || isLinkExpired === '1' || isLinkExpired === true;
  }

  public get patientInitials(): string {
    if (!this.patientName) {
      return 'PT';
    }
    const words = this.patientName.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      return 'PT';
    }
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
  }

  public get statusBadgeClass(): string {
    const status = String(this.paymentStatus || '').toLowerCase();

    if (this.pfStatus === 3 || status.includes('overdue') || status.includes('late')) {
      return 'status-badge--overdue';
    }
    if (this.pfStatus === 4 || status.includes('complete') || status.includes('paid')) {
      return 'status-badge--completed';
    }
    if (this.pfStatus === 5 || status.includes('cancel')) {
      return 'status-badge--canceled';
    }
    if (this.pfStatus === 1 || this.pfStatus === 2 || status.includes('active')) {
      return 'status-badge--active';
    }
    return 'status-badge--pending';
  }

  public get statusHeadlineSuffix(): string {
    const status = String(this.paymentStatus || '').toLowerCase();

    if (this.pfStatus === 4 || status.includes('complete') || status.includes('paid')) {
      return 'has been paid.';
    }
    if (this.pfStatus === 5 || status.includes('cancel')) {
      return 'has been canceled.';
    }
    if (this.pfStatus === 3 || status.includes('overdue') || status.includes('late')) {
      return 'is overdue.';
    }
    return 'is awaiting payment.';
  }

  private applyPaymentStatus(sprData: any): void {
    this.pfStatus = this.toNumberOrNull(sprData.pf_status ?? sprData.status);

    const statusText = sprData.status_text ?? sprData.pf_status_text ?? sprData.payment_status;
    if (statusText !== null && statusText !== undefined && statusText !== '') {
      this.paymentStatus = this.formatStatusLabel(statusText);
      return;
    }

    if (this.pfStatus !== null) {
      this.paymentStatus = this.mapPfStatusLabel(this.pfStatus);
    }
  }

  private mapPfStatusLabel(pfStatus: number): string {
    if (pfStatus === 1 || pfStatus === 2) {
      return 'Active';
    }
    if (pfStatus === 3) {
      return 'Overdue';
    }
    if (pfStatus === 4) {
      return 'Completed';
    }
    if (pfStatus === 5) {
      return 'Canceled';
    }
    return 'Pending';
  }

  private formatStatusLabel(value: unknown): string {
    const status = String(value).trim();
    if (!status) {
      return 'Pending';
    }

    const numericStatus = Number(status);
    if (Number.isFinite(numericStatus) && String(numericStatus) === status) {
      return this.mapPfStatusLabel(numericStatus);
    }

    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  private resolvePlanDuration(data: any): string | null {
    const months = data?.plan_duration ?? data?.contract_duration ?? data?.duration_months ?? data?.no_of_months;
    if (months === null || months === undefined || months === '') {
      return null;
    }

    const parsed = Number(months);
    if (Number.isFinite(parsed)) {
      return parsed === 1 ? '1 month' : `${parsed} months`;
    }

    return String(months);
  }

  private resolvePatientName(data: any): string {
    const firstName = data?.patient_first_name || data?.first_name || '';
    const lastName = data?.patient_last_name || data?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || data?.patient_name || 'Patient';
  }

  private resolvePatientPhone(data: any): string {
    const phone = data?.patient_mobile || data?.mobile || '';
    if (!phone) {
      return '';
    }
    const countryCode = data?.patient_mobile_country_code || data?.mobile_country_code || '';
    return `${countryCode}${phone}`;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
